import React, { useState, useContext, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api";
import { ThemeContext } from "../App";

const OTP_LENGTH = 6;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";
  const initialOtp = searchParams.get("otp") || "";
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState(initialOtp);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);

  const classes = useMemo(() => {
    const baseBackground = theme === "dark" ? "bg-slate-950 text-white" : "bg-slate-50 text-gray-900";
    const card = theme === "dark" ? "bg-gray-800 text-white" : "bg-white text-gray-900 border border-gray-200";
    const input =
      theme === "dark"
        ? "w-full p-2 mb-4 rounded bg-gray-900 border border-gray-600 text-white placeholder-gray-400"
        : "w-full p-2 mb-4 rounded bg-white border border-gray-300 text-gray-900 placeholder-gray-500";
    const successBox = theme === "dark" ? "bg-emerald-700 text-white" : "bg-emerald-100 text-emerald-800";
    const errorBox = theme === "dark" ? "bg-red-700 text-white" : "bg-red-100 text-red-800";
    return { baseBackground, card, input, successBox, errorBox };
  }, [theme]);

  const handleSubmit = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedOtp = otp.trim();

    setStatus(null);

    if (!trimmedEmail) {
      setStatus({ type: "error", message: "Enter your registered email." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus({ type: "error", message: "Enter a valid email address." });
      return;
    }
    if (!trimmedOtp) {
      setStatus({ type: "error", message: "Enter the verification code sent to your email." });
      return;
    }
    if (trimmedOtp.length !== OTP_LENGTH) {
      setStatus({ type: "error", message: `Verification code must be ${OTP_LENGTH} digits.` });
      return;
    }
    if (!password || !confirmPassword) {
      setStatus({ type: "error", message: "Enter and confirm your new password." });
      return;
    }
    if (password.length < 6) {
      setStatus({ type: "error", message: "Password must be at least 6 characters long." });
      return;
    }
    if (password !== confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match." });
      return;
    }

    try {
      setLoading(true);
      setStatus(null);
      await resetPassword({ email: trimmedEmail, otp: trimmedOtp, newPassword: password });
      setEmail(trimmedEmail);
      setStatus({ type: "success", message: "Password reset successful! Redirecting to login..." });
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      console.error("Reset password error", err);
      setStatus({ type: "error", message: err.response?.data?.detail || "Reset failed. Request a new link." });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className={`flex justify-center items-center min-h-screen transition-colors duration-200 ${classes.baseBackground}`}>
      <div className={`p-8 rounded-lg shadow-lg w-96 transition-colors duration-200 ${classes.card}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Reset Password</h2>
          <button className="text-sm px-3 py-1 rounded border border-current" onClick={toggleTheme}>
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {status && (
          <div className={`${status.type === "error" ? classes.errorBox : classes.successBox} p-2 rounded mb-4 text-center`}>
            {status.message}
          </div>
        )}

        <input
          type="email"
          placeholder="Registered email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={loading}
          autoFocus={!email}
        />
        <input
          type="text"
          inputMode="numeric"
          placeholder="Verification code (OTP)"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D+/g, "").slice(0, OTP_LENGTH))}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={loading}
        />
        <input
          type="password"
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={loading}
        />
        <input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={loading}
        />

        <button
          className="w-full bg-blue-500 p-2 rounded font-bold hover:bg-blue-600 text-white mb-4 disabled:opacity-60"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update Password"}
        </button>

        <div className="text-center">
          <Link to="/" className="text-blue-500 hover:underline">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
